/**
 * RelinkDialog — shown when linked folders are missing (deleted or cross-tenant).
 * Works like a form: user makes all changes, then clicks Apply to submit.
 */
import { useState } from 'react'
import { AlertTriangle, FolderOpen, Trash2, X, Undo2, Check } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, Tooltip } from '@papan/core-ui/ui'
import type { WorkspaceLink } from '@papan/workspace/types'

/** Truncated text; the shared <Tooltip> shows the full text when it overflows (hover, or a tap on touch) */
export function Truncated({ text, className }: { text: string; className?: string }) {
  return (
    <Tooltip content={text} placement="top" className={`block truncate cursor-default ${className ?? ''}`}>
      {text}
    </Tooltip>
  )
}

type PendingAction =
  | { type: 'relink'; newTargetFolderId: string; newName?: string }
  | { type: 'remove' }

interface RelinkDialogProps {
  missingLinks: WorkspaceLink[]
  onApply: (decisions: Array<{ linkId: string; action: PendingAction }>) => void
  onClose: () => void
  /** Open a folder picker and return the selected folder ID + name */
  onPickFolder: (hint?: string) => Promise<{ id: string; name: string } | null>
}

export function RelinkDialog({ missingLinks, onApply, onClose, onPickFolder }: RelinkDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [pending, setPending] = useState<Map<string, PendingAction>>(new Map())
  const [picking, setPicking] = useState<string | null>(null)

  if (missingLinks.length === 0) return null

  const setAction = (linkId: string, action: PendingAction) => {
    setPending(prev => new Map(prev).set(linkId, action))
  }

  const clearAction = (linkId: string) => {
    setPending(prev => {
      const next = new Map(prev)
      next.delete(linkId)
      return next
    })
  }

  const handlePickFolder = async (linkId: string, pathHint?: string) => {
    setPicking(linkId)
    const result = await onPickFolder(pathHint)
    if (result) {
      setAction(linkId, { type: 'relink', newTargetFolderId: result.id, newName: result.name })
    }
    setPicking(null)
  }

  const [confirmRemoval, setConfirmRemoval] = useState(false)

  const unresolvedLinks = missingLinks.filter(l => !pending.has(l.id))

  const doApply = () => {
    const decisions: Array<{ linkId: string; action: PendingAction }> = []
    for (const link of missingLinks) {
      const action = pending.get(link.id)
      decisions.push({ linkId: link.id, action: action ?? { type: 'remove' } })
    }
    onApply(decisions)
  }

  const handleApply = () => {
    if (unresolvedLinks.length > 0) {
      setConfirmRemoval(true)
    } else {
      doApply()
    }
  }

  void pending.size

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />

      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[420px] border themed-border`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b themed-border">
          <AlertTriangle size={16} className="text-amber-400" />
          <h3 className="text-sm font-medium themed-text">Missing Linked Folders</h3>
          <div className="flex-1" />
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs themed-text-muted">
            The following linked folders could not be found. Choose a new folder or remove the link.
          </p>

          {missingLinks.map(link => {
            const action = pending.get(link.id)
            const isRemoved = action?.type === 'remove'
            const isRelinked = action?.type === 'relink'

            return (
              <div key={link.id} className={`px-3 py-2 rounded themed-bg-tertiary border themed-border space-y-1.5 ${isRemoved ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-2">
                  <FolderOpen size={14} className={isRelinked ? 'text-green-400 shrink-0' : 'text-amber-400 shrink-0'} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm themed-text truncate ${isRemoved ? 'line-through' : ''}`}>
                      {isRelinked
                        ? (action as any).newName || 'Selected folder'
                        : link.alias || (link.path ? link.path.split('/').pop() : 'Unknown folder')
                      }
                    </p>
                    <p className="text-[10px] themed-text-muted truncate">
                      {link.path || `ID: ${link.targetFolderId.slice(0, 12)}...`}
                    </p>
                  </div>
                  {action ? (
                    <button
                      onClick={() => clearAction(link.id)}
                      className="text-xs p-1 rounded themed-text-muted hover:themed-text transition-colors"
                      title="Undo"
                    >
                      <Undo2 size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={() => setAction(link.id, { type: 'remove' })}
                      className="text-xs p-1 rounded themed-text-muted hover:text-red-400 transition-colors"
                      title="Remove link"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>

                {!isRemoved && !isRelinked && (
                  <>
                    {link.resolvedId ? (
                      <div className="pl-5 space-y-1.5">
                        <Truncated className="text-[11px] text-green-400" text={`Found at ${link.path}`} />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setAction(link.id, { type: 'relink', newTargetFolderId: link.resolvedId! })}
                            className="text-xs px-2 py-1 rounded bg-green-600/30 text-green-300 hover:bg-green-600/50 transition-colors"
                          >
                            Link here
                          </button>
                          <button
                            onClick={() => handlePickFolder(link.id, link.path)}
                            disabled={picking === link.id}
                            className="text-xs px-2 py-1 rounded themed-bg-secondary themed-text-muted hover:themed-text transition-colors disabled:opacity-50"
                          >
                            Pick other
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="pl-5 flex items-center gap-2">
                        <p className="text-[11px] themed-text-muted flex-1">Folder not found</p>
                        <button
                          onClick={() => handlePickFolder(link.id, link.path)}
                          disabled={picking === link.id}
                          className="text-xs px-2 py-1 rounded bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 transition-colors disabled:opacity-50"
                        >
                          {picking === link.id ? 'Picking...' : 'Re-link'}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {isRelinked && (
                  <div className="pl-5">
                    <p className="text-[11px] text-green-400 flex items-center gap-1">
                      <Check size={10} /> Will link to: {(action as any).newName || 'selected folder'}
                    </p>
                  </div>
                )}

                {isRemoved && (
                  <div className="pl-5">
                    <p className="text-[11px] text-red-400">Will be removed</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Confirm removal warning */}
        {confirmRemoval && (
          <div className="px-4 py-3 border-t themed-border bg-red-500/10">
            <p className="text-xs text-red-400 mb-2">
              {unresolvedLinks.length} unresolved link{unresolvedLinks.length > 1 ? 's' : ''} will be removed:
            </p>
            <ul className="text-xs text-red-300 mb-3 pl-4 list-disc">
              {unresolvedLinks.map(l => (
                <li key={l.id} className="truncate">
                  {l.alias || (l.path ? l.path.split('/').pop() : 'Unknown')}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmRemoval(false)}
                className="px-3 py-1.5 text-xs rounded themed-text-secondary hover:themed-text transition-colors"
              >
                Go back
              </button>
              <button
                onClick={doApply}
                className="px-3 py-1.5 text-xs rounded bg-red-600/80 text-white hover:bg-red-600 transition-colors"
              >
                Remove and apply
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!confirmRemoval && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
            <button
              onClick={() => missingLinks.forEach(l => setAction(l.id, { type: 'remove' }))}
              className="px-3 py-1.5 text-sm rounded themed-text-muted hover:text-red-400 transition-colors"
            >
              Remove all
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded themed-text-secondary hover:themed-text transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-3 py-1.5 text-sm rounded bg-purple-600/80 text-white hover:bg-purple-600 transition-colors"
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
